import type { TaskBodyProps } from '../index';
import type { TimelineEvent } from '../../../patient-files/actions/types';
import { approveDraftFromLanding } from '../../../patient-files/actions/dx-report/landing/approveDraft';
import { extractPayloadFromEvent } from '../../../patient-files/actions/dx-report/editor/payloadHelpers';
import { isPayloadSubmittable } from '../../../patient-files/actions/dx-report/editor/editorState';
import { designLoopActions } from '../design-dx/designLoop';
import { handBackTarget } from '../design-dx/handBackNotes';
import { ReviewDecisionFooter, type ReviewDecisionCopy } from './ReviewDecisionFooter';
import type { DraftNote } from './useDraftNotes';

const COPY: ReviewDecisionCopy = {
    source: 'FinalReviewTask',
    targetLabel: 'Hands back',
    handBack: 'Hand back',
    handingBack: 'Handing back…',
    handBackError: 'Failed to hand back. Please try again.',
    approve: 'Approve',
    approveError: 'Failed to approve the Dx report. Please try again.'
};

function approveBlocker(notes: readonly DraftNote[], draft: TimelineEvent | null): string | null {
    if (notes.length > 0) return 'Remove the notes to approve, or hand back';
    if (!draft) return 'No Dx report is waiting for review';
    const payload = extractPayloadFromEvent(draft);
    if (!payload || !isPayloadSubmittable(payload)) {
        return 'The Dx draft is missing required sections; hand it back to finish it';
    }
    return null;
}

type FinalReviewFooterProps = Pick<TaskBodyProps, 'statusMeta' | 'onTaskComplete'> & {
    caseId: string;
    draft: TimelineEvent | null;
    notes: DraftNote[];
};

export function FinalReviewFooter({
    caseId,
    statusMeta,
    onTaskComplete,
    draft,
    notes
}: FinalReviewFooterProps) {
    const target = notes.length > 0 ? handBackTarget(notes) : null;
    const { handBackActions } = designLoopActions(statusMeta.macro);
    return (
        <ReviewDecisionFooter
            caseId={caseId}
            statusMeta={statusMeta}
            onTaskComplete={onTaskComplete}
            notes={notes}
            handBackAction={handBackActions[target === 'dx' ? 'dx' : 'design']}
            handBackTarget={target}
            approve={draft && (() => approveDraftFromLanding(caseId, draft.id))}
            approveHint={approveBlocker(notes, draft)}
            copy={COPY}
        />
    );
}
